// Cuerpo de letra de una baldosa de rubro: el más grande (hasta `max`) con el que el nombre entra en
// la baldosa sin cortar palabras. Devuelve `cqi` (% del ancho de contenido de la baldosa) para que la
// tipografía escale con la grilla y siga entrando en pantallas intermedias.

const CHAR_EM = 0.53; // ancho medio de una letra de Arboria Bold con su interletra, en em (medido)
const LINE_EM = 0.95; // interlineado de los nombres
const GO = 52; // flecha de la esquina + aire
const DESC = 50; // bajada de dos líneas + margen

/**
 * Cantidad de líneas que ocupan `words` con letras de `fs` px en `width` px. La flecha flota arriba a
 * la derecha: las líneas que caen a su altura tienen `reserve` px menos.
 */
function lineCount(words, fs, width, reserve = 0) {
  const narrowLines = reserve ? Math.ceil(GO / (fs * LINE_EM)) : 0;
  const widthOf = (line) => (line <= narrowLines ? width - reserve : width);
  let lines = 1;
  let used = 0;
  for (const word of words) {
    const w = word.length * CHAR_EM * fs;
    const next = used === 0 ? w : used + CHAR_EM * fs + w;
    if (next <= widthOf(lines)) {
      used = next;
      continue;
    }
    if (used === 0) return Infinity;
    lines += 1;
    if (w > widthOf(lines)) return Infinity;
    used = w;
  }
  return lines;
}

/** Cuerpo (px) y líneas con que `name` entra en la baldosa. */
function fitTile(name, { w, h, max, pad, go = false, desc = false }) {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean);
  const content = w - 2 * pad;
  const height = h - 2 * pad - (desc ? DESC : 0);
  let fs = max;
  let lines = lineCount(words, fs, content, go ? GO : 0);
  while (fs > 14 && lines * fs * LINE_EM > height) {
    fs -= 1;
    lines = lineCount(words, fs, content, go ? GO : 0);
  }
  return { fs, lines, content };
}

export function fitTileFont(name, slot) {
  const { fs, content } = fitTile(name, slot);
  return Math.round((fs / content) * 10000) / 100;
}

/**
 * Reparte los nombres (en orden de importancia) entre las baldosas para que, en conjunto, la letra
 * quede lo más grande posible y en pocos renglones: «Uñas» va a la baldosa alta y angosta y «Centro
 * de estética» a una ancha, como en la propuesta. A igual resultado, el nombre más importante va a la
 * baldosa más grande. Asignación óptima por programación dinámica sobre subconjuntos
 * (hasta 12 nombres). Devuelve, para cada baldosa, el índice del nombre que le toca (o -1).
 */
export function assignTiles(names, slots) {
  const n = Math.min(names.length, slots.length, 12);
  const k = Math.min(slots.length, n);
  const score = slots.slice(0, k).map((slot) =>
    names.slice(0, n).map((name, j) => {
      // Cuánto llena la baldosa, menos un castigo por cada renglón de más; a igualdad, el más importante.
      const { fs, lines } = fitTile(name, slot);
      return fs / slot.max - 0.15 * (lines - 1) - j * 1e-4 * (slot.max / 100);
    })
  );
  const size = 1 << n;
  const best = new Float64Array(size).fill(-Infinity);
  const pick = new Int8Array(size).fill(-1);
  best[0] = 0;
  const bits = (m) => {
    let c = 0;
    for (let x = m; x; x &= x - 1) c += 1;
    return c;
  };
  for (let mask = 0; mask < size; mask += 1) {
    const s = bits(mask);
    if (s >= k || best[mask] === -Infinity) continue;
    for (let j = 0; j < n; j += 1) {
      if (mask & (1 << j)) continue;
      const next = mask | (1 << j);
      const value = best[mask] + score[s][j];
      if (value > best[next]) {
        best[next] = value;
        pick[next] = j;
      }
    }
  }
  let end = -1;
  for (let mask = 0; mask < size; mask += 1) {
    if (bits(mask) === k && (end === -1 || best[mask] > best[end])) end = mask;
  }
  const result = new Array(slots.length).fill(-1);
  for (let mask = end, s = k - 1; mask > 0; s -= 1) {
    const j = pick[mask];
    result[s] = j;
    mask &= ~(1 << j);
  }
  return result;
}
